import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AnimeCard from "@/components/feature/AnimeCard";
import Pagination from "@/components/feature/Pagination";
import CategoryFilter from "@/pages/category/components/CategoryFilter";
import { animeList } from "@/mocks/anime";
import { filterAnime, getCategoryById } from "@/utils/anime";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryId = "" } = useParams();
  const isAll = categoryId === "all";
  const category = getCategoryById(categoryId);
  const valid = isAll || Boolean(category);
  const displayName = isAll ? "全部动漫" : category?.name ?? "";
  const displayDesc = isAll
    ? "浏览本站全部动漫作品，覆盖热血、校园、科幻、奇幻、恋爱等丰富类型，总有一部适合你。"
    : category?.description ?? "";

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("update");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setSort("update");
    setPage(1);
  }, [categoryId]);

  useDocumentMeta(
    valid ? `${displayName} - 动漫免费在观看` : "分类 - 动漫免费在观看",
    valid
      ? `${displayName}分类动漫在线观看，${displayDesc}尽在动漫免费在观看，海量高清动漫资源免费畅享。`
      : "浏览动漫免费在观看的全部动漫分类，快速找到你想看的高清动漫作品。",
    valid ? `${displayName},动漫在线观看,免费动漫,动漫免费在观看` : "动漫分类,在线观看动漫",
  );

  const result = useMemo(
    () => filterAnime(animeList, isAll ? null : category?.name ?? "", { region, year, sort }),
    [isAll, category, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = result.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setSort("update");
    setPage(1);
  };

  if (!valid) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </div>
        <h1 className="mt-4 font-heading font-bold text-xl text-foreground-950">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的分类不存在，去首页看看其他内容吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors flex items-center gap-1">
          <i className="ri-home-4-line"></i>
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900 font-medium">{displayName}</span>
      </nav>

      <div className="mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          {isAll ? "全部动漫" : `${displayName}动漫`}
        </h1>
        <p className="mt-2 text-sm text-foreground-500 max-w-2xl leading-relaxed">
          {displayDesc}
        </p>
      </div>

      <button
        type="button"
        onClick={() => setFilterOpen((v) => !v)}
        className="lg:hidden w-full flex items-center justify-between px-4 py-3 mb-4 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-500"></i>
          筛选条件
        </span>
        <i className={`ri-arrow-down-s-line transition-transform ${filterOpen ? "rotate-180" : ""}`}></i>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:gap-8 items-start">
        <div className={`${filterOpen ? "block" : "hidden"} lg:block lg:sticky lg:top-24`}>
          <CategoryFilter
            categoryId={categoryId}
            region={region}
            year={year}
            sort={sort}
            onRegionChange={(v) => { setRegion(v); setPage(1); }}
            onYearChange={(v) => { setYear(v); setPage(1); }}
            onSortChange={(v) => { setSort(v); setPage(1); }}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-foreground-500">
              共找到 <span className="text-primary-600 font-semibold">{result.length}</span> 部作品
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>

          {paged.length === 0 ? (
            <div className="bg-background-50 border border-background-200 rounded-lg py-20 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-3xl"></i>
              </div>
              <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">暂无符合条件的动漫</h2>
              <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {paged.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}